import { startLoading, stopLoading } from '@labrav/common'
import { AxiosError, AxiosResponse } from 'axios'
import type { AppDispatch, RootState } from '../../../store'
import { updateAppAlert } from '../../../common/modules/appAlert/actions'
import { getErrorMessages } from '../../../common/utils/error'
import { errorLogger } from '../../../common/utils/errorLogger'
import { RequestFailureMessage } from '../../../common/utils/messagesContants'
import {
  createPartnerTeamCollection,
  fetchPartnerContactsDetails,
  fetchPartnerTeamCollection,
  fetchPartnerTeamCollections,
  TeamCollectionApi,
  updatePartnerTeamCollection,
  UpdateTeamCollectionPayload,
} from '../../api/teamRelationship'
import { mapContactDetailsToTeamMembers } from './mapContacts'
import {
  mapCollectionApiMembersToTeamMembers,
  mapTeamCollectionApiToTeamCollection,
  mapTeamCollectionApisToTeamCollections,
  mergeTeamCollectionApiIntoExisting,
} from './mapCollections'
import {
  TEAM_RELATIONSHIP_COLLECTION_DETAIL_LOADING,
  TEAM_RELATIONSHIP_COLLECTIONS_LOADING,
  TEAM_RELATIONSHIP_CONTACTS_LOADING,
  TEAM_RELATIONSHIP_CREATE_COLLECTION_LOADING,
  TEAM_RELATIONSHIP_UPDATE_COLLECTION_LOADING,
  clearSelectedCollection,
  closeCreateCollection,
  renameCollection,
  selectCollection,
  setCollectionArchived,
  setCollections,
  setMembers,
  updateCollectionMemberIds,
  upsertCollection,
  upsertMembers,
} from './action'

const reportTeamRelationshipError =
  (error: unknown) => (dispatch: AppDispatch, getState: () => RootState) => {
    const e = error as AxiosError
    const errorResponse = e?.response as AxiosResponse<ErrorResponse>

    dispatch(
      updateAppAlert({
        message: getErrorMessages([RequestFailureMessage])(errorResponse),
        messageType: 'ERROR',
        autoClose: true,
      })
    )
    const globalState = getState()
    errorLogger({ globalState })(error as Error, errorResponse?.data?.track_id)
  }

const applyTeamCollectionApi =
  (
    collectionApi: TeamCollectionApi,
    options: { mergeExisting?: boolean } = {}
  ) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const existing =
      getState().teamRelationship.collectionsById[collectionApi.id]
    const collection =
      options.mergeExisting && existing
        ? mergeTeamCollectionApiIntoExisting(existing, collectionApi)
        : mapTeamCollectionApiToTeamCollection(collectionApi)

    if (collection) {
      dispatch(upsertCollection(collection))
    }
    if (collectionApi.members?.length) {
      dispatch(
        upsertMembers(
          mapCollectionApiMembersToTeamMembers(collectionApi.members)
        )
      )
    }
  }

export const getTeamRelationshipCollections =
  (partnerId: string, params?: { skip?: number; limit?: number }) =>
  async (dispatch: AppDispatch): Promise<boolean> => {
    if (!partnerId) {
      return false
    }

    dispatch(startLoading(TEAM_RELATIONSHIP_COLLECTIONS_LOADING))
    try {
      const response = await fetchPartnerTeamCollections(
        partnerId,
        params ?? {}
      )
      const collections = mapTeamCollectionApisToTeamCollections(
        response.data?.collections ?? []
      )
      dispatch(setCollections(collections))
      return true
    } catch (error: unknown) {
      dispatch(reportTeamRelationshipError(error))
      return false
    } finally {
      dispatch(stopLoading(TEAM_RELATIONSHIP_COLLECTIONS_LOADING))
    }
  }

export const getTeamRelationshipCollectionDetail =
  (partnerId: string, collectionId: string) =>
  async (dispatch: AppDispatch): Promise<boolean> => {
    if (!partnerId || !collectionId) {
      return false
    }

    dispatch(startLoading(TEAM_RELATIONSHIP_COLLECTION_DETAIL_LOADING))
    try {
      const response = await fetchPartnerTeamCollection(partnerId, collectionId)
      dispatch(applyTeamCollectionApi(response.data))
      return true
    } catch (error: unknown) {
      dispatch(reportTeamRelationshipError(error))
      return false
    } finally {
      dispatch(stopLoading(TEAM_RELATIONSHIP_COLLECTION_DETAIL_LOADING))
    }
  }

export const openTeamCollection =
  (partnerId: string, collectionId: string) =>
  async (dispatch: AppDispatch): Promise<boolean> => {
    if (!partnerId || !collectionId) {
      return false
    }
    dispatch(selectCollection(collectionId))
    return await dispatch(
      getTeamRelationshipCollectionDetail(partnerId, collectionId)
    )
  }

const updateTeamCollection =
  (
    partnerId: string,
    collectionId: string,
    payload: UpdateTeamCollectionPayload,
    onSuccess?: (dispatch: AppDispatch) => void
  ) =>
  async (dispatch: AppDispatch): Promise<boolean> => {
    if (!partnerId || !collectionId) {
      return false
    }

    dispatch(startLoading(TEAM_RELATIONSHIP_UPDATE_COLLECTION_LOADING))
    try {
      const response = await updatePartnerTeamCollection(
        partnerId,
        collectionId,
        payload
      )
      if (response.data?.id) {
        dispatch(applyTeamCollectionApi(response.data, { mergeExisting: true }))
      } else {
        if (payload.name !== undefined) {
          dispatch(renameCollection(collectionId, payload.name))
        }
        if (payload.memberIds !== undefined) {
          dispatch(updateCollectionMemberIds(collectionId, payload.memberIds))
        }
        if (payload.isArchived !== undefined) {
          dispatch(
            setCollectionArchived(collectionId, {
              isArchived: payload.isArchived,
            })
          )
        }
      }
      onSuccess?.(dispatch)
      return true
    } catch (error: unknown) {
      dispatch(reportTeamRelationshipError(error))
      return false
    } finally {
      dispatch(stopLoading(TEAM_RELATIONSHIP_UPDATE_COLLECTION_LOADING))
    }
  }

export const renameTeamCollection =
  (partnerId: string, collectionId: string, name: string) =>
  async (dispatch: AppDispatch): Promise<boolean> => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      return false
    }
    return await dispatch(
      updateTeamCollection(partnerId, collectionId, { name: trimmedName })
    )
  }

export const updateTeamCollectionMembers =
  (partnerId: string, collectionId: string, memberIds: string[]) =>
  async (dispatch: AppDispatch): Promise<boolean> =>
    await dispatch(updateTeamCollection(partnerId, collectionId, { memberIds }))

export const archiveSelectedCollection =
  (partnerId: string) =>
  async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<boolean> => {
    const { selectedCollectionId, collectionsById } =
      getState().teamRelationship
    if (!selectedCollectionId || !collectionsById[selectedCollectionId]) {
      return false
    }
    return await dispatch(
      updateTeamCollection(
        partnerId,
        selectedCollectionId,
        { isArchived: true },
        nextDispatch => {
          nextDispatch(clearSelectedCollection())
        }
      )
    )
  }

export const unarchiveSelectedCollection =
  (partnerId: string) =>
  async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<boolean> => {
    const { selectedCollectionId, collectionsById } =
      getState().teamRelationship
    if (!selectedCollectionId || !collectionsById[selectedCollectionId]) {
      return false
    }
    return await dispatch(
      updateTeamCollection(partnerId, selectedCollectionId, {
        isArchived: false,
      })
    )
  }

export const createCollectionFromDraft =
  (partnerId: string) =>
  async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<boolean> => {
    const { draft } = getState().teamRelationship
    const name = draft.name.trim()
    if (!partnerId || !draft.type || !name) {
      return false
    }

    const memberIds = [...draft.selectedMemberIds]
    const collectionType = draft.type
    const createdByUserId = getState().userProfile?.userProfile?.id || ''

    dispatch(startLoading(TEAM_RELATIONSHIP_CREATE_COLLECTION_LOADING))
    try {
      await createPartnerTeamCollection(partnerId, {
        name,
        type: collectionType,
        memberIds,
        createdBy: createdByUserId,
      })
      const refreshed = await dispatch(
        getTeamRelationshipCollections(partnerId)
      )
      if (!refreshed) {
        return false
      }
      dispatch(closeCreateCollection())
      return true
    } catch (error: unknown) {
      dispatch(reportTeamRelationshipError(error))
      return false
    } finally {
      dispatch(stopLoading(TEAM_RELATIONSHIP_CREATE_COLLECTION_LOADING))
    }
  }

export const getTeamRelationshipContacts =
  (partnerId: string) =>
  async (dispatch: AppDispatch): Promise<boolean> => {
    if (!partnerId) {
      return false
    }

    dispatch(startLoading(TEAM_RELATIONSHIP_CONTACTS_LOADING))
    try {
      const response = await fetchPartnerContactsDetails(partnerId)
      const contacts = response.data?.contacts ?? []
      dispatch(setMembers(mapContactDetailsToTeamMembers(contacts)))
      return true
    } catch (error: unknown) {
      dispatch(reportTeamRelationshipError(error))
      return false
    } finally {
      dispatch(stopLoading(TEAM_RELATIONSHIP_CONTACTS_LOADING))
    }
  }
